import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity, AlertTriangle, Bug, Building2, CheckCircle2, CreditCard,
  MailWarning, RefreshCw, RotateCcw, Search, Trash2, Users,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import Button from '../ui/Button';
import AdminEmailEditor from './AdminEmailEditor';
import DeleteOrganisationDialog from './DeleteOrganisationDialog';
import CultivarClaimsPanel from './CultivarClaimsPanel';
import CultivarMovePanel from './CultivarMovePanel';
import cultivarClaimsService, { type ClaimsSummary } from '../../services/cultivarClaims';
import platformService from '../../services/platform';
import type {
  ClientErrorReport, ClientErrorSummary, PlatformOrganisation,
  PlatformOverview as Overview, PlatformUser, PlatformUserFilter,
} from '../../services/platform';
import { useOrgTimezone } from '../../utils/timezone';
import { formatDateInTz, formatDateTimeInTz } from '../../utils/timezone';
import { cn } from '../../utils/cn';

type Tab = 'overview' | 'organisations' | 'users' | 'errors' | 'claims' | 'move';

const TABS: Tab[] = ['overview', 'organisations', 'users', 'errors', 'claims', 'move'];

// support@'s "rights to review" email links to ?tab=claims.
function initialTab(): Tab {
  try {
    const t = new URLSearchParams(window.location.search).get('tab');
    return TABS.includes(t as Tab) ? (t as Tab) : 'overview';
  } catch {
    return 'overview';
  }
}

/** "3 days ago" / "never" — the question being asked of last_active_at is how stale it is, not when exactly. */
function relativeAge(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return 'unknown';
  const mins = Math.floor(ms / 60000);
  if (mins < 2) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return months < 12 ? `${months}mo ago` : `${Math.floor(months / 12)}y ago`;
}

function fullName(u: { first_name?: string | null; last_name?: string | null; username: string }): string {
  return [u.first_name, u.last_name].filter(Boolean).join(' ') || u.username;
}

function StatTile({
  label, value, icon, tone = 'neutral', hint,
}: {
  label: string;
  value: number | string;
  icon: React.ReactNode;
  tone?: 'neutral' | 'warning' | 'success';
  hint?: string;
}) {
  const tones = {
    neutral: 'bg-white border-neutral-200',
    warning: 'bg-amber-50 border-amber-300',
    success: 'bg-emerald-50 border-emerald-200',
  };
  const iconTones = {
    neutral: 'bg-neutral-100 text-neutral-600',
    warning: 'bg-amber-200 text-amber-800',
    success: 'bg-emerald-100 text-emerald-700',
  };
  return (
    <div className={cn('rounded-xl border p-4 min-w-0', tones[tone])}>
      <div className="flex items-start gap-3 min-w-0">
        <div className={cn('shrink-0 rounded-lg p-2', iconTones[tone])}>{icon}</div>
        <div className="min-w-0">
          <p className="text-2xl font-semibold text-neutral-900 leading-tight">{value}</p>
          <p className="text-xs text-neutral-600 mt-0.5 break-words">{label}</p>
          {hint && <p className="text-[11px] text-neutral-500 mt-1 break-words">{hint}</p>}
        </div>
      </div>
    </div>
  );
}

/** Signups per day. A plain bar strip — enough to see a spike, not worth a chart library. */
function SignupTrend({ trend, days }: { trend: { day: string; signups: number }[]; days: number }) {
  const max = Math.max(1, ...trend.map(d => d.signups));
  const total = trend.reduce((sum, d) => sum + d.signups, 0);

  if (!trend.length) {
    return <p className="text-sm text-neutral-500">No signups in the last {days} days.</p>;
  }

  return (
    <div className="min-w-0">
      <p className="text-sm text-neutral-600 mb-3">
        <span className="font-semibold text-neutral-900">{total}</span> signup{total === 1 ? '' : 's'} in the last {days} days
      </p>
      <div className="flex items-end gap-0.5 h-20 overflow-x-auto">
        {trend.map(d => (
          <div
            key={d.day}
            title={`${d.day}: ${d.signups}`}
            className="flex-1 min-w-[3px] bg-primary-500 rounded-t-sm hover:bg-primary-600 transition-colors"
            style={{ height: `${Math.max(4, (d.signups / max) * 100)}%` }}
          />
        ))}
      </div>
    </div>
  );
}

function PlatformOverviewPanel() {
  const tz = useOrgTimezone();
  const [tab, setTab] = useState<Tab>(initialTab);
  const [claimsSummary, setClaimsSummary] = useState<ClaimsSummary | null>(null);
  // Bumped by Refresh so the claims panel loads again.
  const [claimsKey, setClaimsKey] = useState(0);

  const [overview, setOverview] = useState<Overview | null>(null);
  const [orgs, setOrgs] = useState<PlatformOrganisation[] | null>(null);
  const [users, setUsers] = useState<PlatformUser[] | null>(null);
  const [userTotal, setUserTotal] = useState(0);
  const [errors, setErrors] = useState<ClientErrorReport[] | null>(null);
  const [errorSummary, setErrorSummary] = useState<ClientErrorSummary | null>(null);
  const [showResolved, setShowResolved] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [deleting, setDeleting] = useState<PlatformOrganisation | null>(null);
  const [deletedNotice, setDeletedNotice] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [userFilter, setUserFilter] = useState<PlatformUserFilter | undefined>();

  const loadOverview = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await platformService.getOverview(30);
    if (res.success) setOverview(res.data);
    else setError(res.error.message);
    setLoading(false);
  }, []);

  const loadOrgs = useCallback(async (term: string) => {
    setLoading(true);
    setError(null);
    const res = await platformService.getOrganisations({ search: term, includeInactive: true });
    if (res.success) setOrgs(res.data);
    else setError(res.error.message);
    setLoading(false);
  }, []);

  const loadErrors = useCallback(async (includeResolved: boolean) => {
    setLoading(true);
    setError(null);
    const [listRes, sumRes] = await Promise.all([
      platformService.getClientErrors({ includeResolved, limit: 100 }),
      platformService.getClientErrorSummary(),
    ]);
    if (listRes.success) setErrors(listRes.data.errors);
    else setError(listRes.error.message);
    if (sumRes.success) setErrorSummary(sumRes.data);
    setLoading(false);
  }, []);

  const toggleResolved = async (report: ClientErrorReport) => {
    const res = await platformService.setClientErrorResolved(report.id, !report.resolved_at);
    if (res.success) loadErrors(showResolved);
    else setError(res.error.message);
  };

  const loadUsers = useCallback(async (term: string, filter?: PlatformUserFilter) => {
    setLoading(true);
    setError(null);
    const res = await platformService.getUsers({ search: term, filter, limit: 100 });
    if (res.success) {
      setUsers(res.data.users);
      setUserTotal(res.data.total);
    } else {
      setError(res.error.message);
    }
    setLoading(false);
  }, []);

  const loadClaimsSummary = useCallback(async () => {
    try {
      const res = await cultivarClaimsService.summary();
      if (res.success) setClaimsSummary(res.data);
    } catch {
      // The count on the tab is a convenience; the tab itself says what failed.
    }
  }, []);

  useEffect(() => { loadClaimsSummary(); }, [loadClaimsSummary]);

  // Debounced so typing in the search box does not fire a request per keystroke.
  useEffect(() => {
    const id = setTimeout(() => {
      if (tab === 'claims' || tab === 'move') { setLoading(false); return; }
      if (tab === 'overview') loadOverview();
      else if (tab === 'organisations') loadOrgs(search);
      else if (tab === 'errors') loadErrors(showResolved);
      else loadUsers(search, userFilter);
    }, search ? 300 : 0);
    return () => clearTimeout(id);
  }, [tab, search, userFilter, showResolved, loadOverview, loadOrgs, loadUsers, loadErrors]);

  const refresh = () => {
    if (tab === 'claims' || tab === 'move') { loadClaimsSummary(); setClaimsKey(k => k + 1); return; }
    if (tab === 'overview') loadOverview();
    else if (tab === 'organisations') loadOrgs(search);
    else if (tab === 'errors') loadErrors(showResolved);
    else loadUsers(search, userFilter);
  };

  const totals = overview?.totals;

  const tabs: { key: Tab; label: string; count?: number }[] = useMemo(() => ([
    { key: 'overview', label: 'Overview' },
    { key: 'organisations', label: 'Organisations', count: totals?.active_orgs },
    { key: 'users', label: 'Users', count: totals?.active_users },
    { key: 'errors', label: 'Errors', count: errorSummary?.unresolved },
    {
      key: 'claims',
      label: 'Cultivar claims',
      count: claimsSummary ? claimsSummary.cultivars + claimsSummary.licensors : undefined,
    },
    { key: 'move', label: 'Move cultivars' },
  ]), [totals, errorSummary, claimsSummary]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-neutral-900">Platform</h2>
          <p className="text-sm text-neutral-500">Every organisation and user on the app</p>
        </div>
        <Button variant="secondary" size="sm" onClick={refresh} disabled={loading}>
          <RefreshCw className={cn('h-4 w-4 mr-2', loading && 'animate-spin')} />
          Refresh
        </Button>
      </div>

      {/* Tabs wrap rather than sit on one unbreakable line — five filter buttons
          on one row is the JobsDashboard bug this project already shipped once. */}
      <div className="flex flex-wrap gap-2">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => { setTab(t.key); setSearch(''); setUserFilter(undefined); }}
            className={cn(
              'px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors',
              tab === t.key
                ? 'bg-neutral-900 text-white border-neutral-900'
                : 'bg-white text-neutral-700 border-neutral-300 hover:border-neutral-400'
            )}
          >
            {t.label}
            {t.count != null && (
              <span className={cn('ml-1.5 text-xs', tab === t.key ? 'text-neutral-300' : 'text-neutral-500')}>
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">{error}</div>
      )}

      {tab === 'claims' && <CultivarClaimsPanel key={claimsKey} onChanged={loadClaimsSummary} />}
      {tab === 'move' && <CultivarMovePanel key={claimsKey} />}

      {/* ─── Overview ─────────────────────────────────────────────────────── */}
      {tab === 'overview' && overview && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <StatTile label="Organisations" value={totals!.active_orgs} icon={<Building2 className="h-4 w-4" />}
              hint={totals!.inactive_orgs ? `${totals!.inactive_orgs} inactive` : undefined} />
            <StatTile label="Active users" value={totals!.active_users} icon={<Users className="h-4 w-4" />}
              hint={totals!.inactive_users ? `${totals!.inactive_users} deactivated` : undefined} />
            <StatTile
              label="No organisation"
              value={totals!.orphan_users}
              icon={<AlertTriangle className="h-4 w-4" />}
              tone={totals!.orphan_users > 0 ? 'warning' : 'neutral'}
              hint={totals!.orphan_users > 0 ? 'Needs triage' : undefined}
            />
            <StatTile label="Unverified email" value={totals!.unverified_users} icon={<MailWarning className="h-4 w-4" />} />
            <StatTile label="Active subscriptions" value={totals!.active_subscriptions}
              icon={<CreditCard className="h-4 w-4" />} tone="success" />
          </div>

          <Card>
            <CardHeader><CardTitle className="text-base">Signups</CardTitle></CardHeader>
            <CardContent>
              <SignupTrend trend={overview.signup_trend} days={overview.window_days} />
            </CardContent>
          </Card>

          {/* The orphan queue. Shown before the recent-signup list because it is
              the only part of this screen with something to do on it. */}
          {overview.orphan_users.length > 0 && (
            <Card className="border-amber-300">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2 text-amber-900">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  <span className="min-w-0">Accounts with no organisation ({overview.orphan_users.length})</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-neutral-600 mb-3">
                  These people signed up without an invitation, so no organisation owns them.
                  Add them to one using the form below, or leave them if they are not customers.
                </p>
                <ul className="divide-y divide-neutral-200">
                  {overview.orphan_users.map(u => (
                    <li key={u.id} className="py-2.5 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-neutral-900 break-words">{fullName(u)}</p>
                        <p className="text-xs text-neutral-500 break-all">{u.email}</p>
                      </div>
                      <div className="text-xs text-neutral-500 shrink-0">
                        {formatDateInTz(u.created_at, tz)}
                        {!u.email_verified && <span className="ml-2 text-amber-700">unverified</span>}
                      </div>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Card>
              <CardHeader><CardTitle className="text-base">Recent signups</CardTitle></CardHeader>
              <CardContent>
                {overview.recent_signups.length === 0 ? (
                  <p className="text-sm text-neutral-500">Nobody has signed up yet.</p>
                ) : (
                  <ul className="divide-y divide-neutral-200">
                    {overview.recent_signups.map(u => (
                      <li key={u.id} className="py-2.5 min-w-0">
                        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                          <p className="text-sm font-medium text-neutral-900 break-words min-w-0">{fullName(u)}</p>
                          <span className="text-xs text-neutral-500 shrink-0">{formatDateInTz(u.created_at, tz)}</span>
                        </div>
                        <p className="text-xs text-neutral-500 break-all">{u.email}</p>
                        <p className="text-xs text-neutral-400 break-words">
                          {u.organisation_name || <span className="text-amber-700">no organisation</span>}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Newest organisations</CardTitle></CardHeader>
              <CardContent>
                {overview.recent_orgs.length === 0 ? (
                  <p className="text-sm text-neutral-500">No organisations yet.</p>
                ) : (
                  <ul className="divide-y divide-neutral-200">
                    {overview.recent_orgs.map(o => (
                      <li key={o.id} className="py-2.5 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-neutral-900 break-words">{o.name}</p>
                          <p className="text-xs text-neutral-500">
                            {o.member_count} member{o.member_count === 1 ? '' : 's'}
                          </p>
                        </div>
                        <span className="text-xs text-neutral-500 shrink-0">{formatDateInTz(o.created_at, tz)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* ─── Organisations ────────────────────────────────────────────────── */}
      {tab === 'organisations' && (
        <div className="space-y-4">
          <SearchBox value={search} onChange={setSearch} placeholder="Search organisations…" />
          {deletedNotice && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800 break-words">{deletedNotice}</div>
          )}
          <div className="bg-white border border-neutral-200 rounded-xl overflow-x-auto relative">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-neutral-600">
                <tr>
                  <Th>Organisation</Th>
                  <Th className="text-right">Users</Th>
                  <Th className="text-right">Seats</Th>
                  <Th>Last active</Th>
                  <Th>Created</Th>
                  <Th><span className="sr-only">Actions</span></Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {(orgs ?? []).map(o => (
                  <tr key={o.id} className="hover:bg-neutral-50">
                    <Td>
                      <span className="font-medium text-neutral-900">{o.name}</span>
                      {!o.is_active && <span className="ml-2 text-xs text-neutral-400">inactive</span>}
                      {o.contact_email && <div className="text-xs text-neutral-500">{o.contact_email}</div>}
                    </Td>
                    <Td className="text-right tabular-nums">{o.active_user_count}</Td>
                    <Td className="text-right tabular-nums whitespace-nowrap">
                      {o.total_seats ? `${o.assigned_seats}/${o.total_seats}` : '—'}
                    </Td>
                    <Td className="whitespace-nowrap text-neutral-600">{relativeAge(o.last_active_at)}</Td>
                    <Td className="whitespace-nowrap text-neutral-600">{formatDateInTz(o.created_at, tz)}</Td>
                    <Td className="text-right">
                      <Button variant="outline" size="sm" onClick={() => { setDeletedNotice(null); setDeleting(o); }}
                        aria-label={`Delete ${o.name}`}>
                        <Trash2 className="h-4 w-4 sm:mr-1.5 text-red-600" />
                        <span className="hidden sm:inline">Delete</span>
                      </Button>
                    </Td>
                  </tr>
                ))}
                {orgs && orgs.length === 0 && <EmptyRow colSpan={6} label="No organisations match." />}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {deleting && (
        <DeleteOrganisationDialog
          orgId={deleting.id}
          orgName={deleting.name}
          orgNames={new Map((orgs ?? []).map(o => [o.id, o.name]))}
          onClose={() => setDeleting(null)}
          onDeleted={(result) => {
            setDeleting(null);
            const d = result.people.deleted.length;
            const m = result.people.moved.length;
            setDeletedNotice(`${result.organisation.name} was deleted. ${d} account${d === 1 ? '' : 's'} deleted, ${m} kept and moved.`);
            loadOrgs(search);
          }}
        />
      )}

      {/* ─── Users ────────────────────────────────────────────────────────── */}
      {tab === 'users' && (
        <div className="space-y-4">
          <SearchBox value={search} onChange={setSearch} placeholder="Search by name, username or email…" />

          <div className="flex flex-wrap gap-2">
            {([
              [undefined, 'All'],
              ['orphans', 'No organisation'],
              ['unverified', 'Unverified'],
              ['inactive', 'Deactivated'],
            ] as [PlatformUserFilter | undefined, string][]).map(([key, label]) => (
              <button
                key={label}
                onClick={() => setUserFilter(key)}
                className={cn(
                  'px-2.5 py-1 rounded-md text-xs font-medium border transition-colors',
                  userFilter === key
                    ? 'bg-primary-600 text-white border-primary-600'
                    : 'bg-white text-neutral-600 border-neutral-300 hover:border-neutral-400'
                )}
              >
                {label}
              </button>
            ))}
          </div>

          {users && (
            <p className="text-xs text-neutral-500">
              Showing {users.length} of {userTotal}
            </p>
          )}

          <div className="bg-white border border-neutral-200 rounded-xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-neutral-600">
                <tr>
                  <Th>User</Th>
                  <Th>Organisation</Th>
                  <Th>Status</Th>
                  <Th>Last active</Th>
                  <Th>Signed up</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {(users ?? []).map(u => (
                  <tr key={u.id} className="hover:bg-neutral-50">
                    <Td>
                      <span className="font-medium text-neutral-900">{fullName(u)}</span>
                      <AdminEmailEditor
                        userId={u.id}
                        email={u.email}
                        className="min-w-[14rem]"
                        onChanged={(email) => setUsers(prev => prev && prev.map(x => (x.id === u.id ? { ...x, email, email_verified: false } : x)))}
                      />
                    </Td>
                    <Td className="text-neutral-600">
                      {u.organisation_name || <span className="text-amber-700">none</span>}
                      {u.org_count > 1 && <span className="text-xs text-neutral-400"> +{u.org_count - 1}</span>}
                    </Td>
                    <Td className="whitespace-nowrap">
                      {!u.is_active && <Pill tone="neutral">deactivated</Pill>}
                      {u.is_active && !u.email_verified && <Pill tone="amber">unverified</Pill>}
                      {u.is_active && u.email_verified && <Pill tone="green">active</Pill>}
                    </Td>
                    <Td className="whitespace-nowrap text-neutral-600">{relativeAge(u.last_active_at)}</Td>
                    <Td className="whitespace-nowrap text-neutral-600" title={formatDateTimeInTz(u.created_at, tz)}>
                      {formatDateInTz(u.created_at, tz)}
                    </Td>
                  </tr>
                ))}
                {users && users.length === 0 && <EmptyRow colSpan={5} label="No users match." />}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ─── Errors ───────────────────────────────────────────────────────── */}
      {tab === 'errors' && (
        <div className="space-y-4">
          {errorSummary && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <StatTile
                label="Unresolved errors"
                value={errorSummary.unresolved}
                icon={<Bug className="h-4 w-4" />}
                tone={errorSummary.unresolved > 0 ? 'warning' : 'success'}
              />
              <StatTile label="Seen in last 24h" value={errorSummary.last_24h} icon={<Activity className="h-4 w-4" />} />
              <StatTile
                label="Occurrences, last 24h"
                value={errorSummary.occurrences_24h}
                icon={<AlertTriangle className="h-4 w-4" />}
                hint="Across all errors"
              />
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-neutral-700">
              <input
                type="checkbox"
                checked={showResolved}
                onChange={e => setShowResolved(e.target.checked)}
                className="rounded border-neutral-300"
              />
              Include resolved
            </label>
            <p className="text-xs text-neutral-500">
              Grouped by fingerprint — one row per distinct bug, not per occurrence.
            </p>
          </div>

          {errors && errors.length === 0 && (
            <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center">
              <CheckCircle2 className="h-6 w-6 text-emerald-600 mx-auto mb-2" />
              <p className="text-sm font-medium text-neutral-900">No unresolved errors</p>
              <p className="text-xs text-neutral-500 mt-1">
                Frontend crashes report themselves here. Silence means silence, not a broken pipe —
                the reporter posts on every caught error.
              </p>
            </div>
          )}

          <div className="space-y-3">
            {(errors ?? []).map(e => (
              <div
                key={e.id}
                className={cn(
                  'bg-white border rounded-xl p-4 min-w-0',
                  e.resolved_at ? 'border-neutral-200 opacity-70' : 'border-red-200'
                )}
              >
                <div className="flex flex-wrap items-start justify-between gap-3 min-w-0">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-xs font-semibold text-red-700 break-all">{e.name}</span>
                      <Pill tone="neutral">{e.source}</Pill>
                      {e.platform && e.platform !== 'web' && <Pill tone="amber">{e.platform}</Pill>}
                      {e.resolved_at && <Pill tone="green">resolved</Pill>}
                    </div>
                    <p className="text-sm text-neutral-900 mt-1.5 break-words">{e.message}</p>
                    <p className="text-xs text-neutral-500 mt-1 break-all">
                      {e.path || '—'}
                      {e.release && <span className="text-neutral-400"> · {e.release}</span>}
                    </p>
                  </div>

                  <div className="text-right shrink-0">
                    <p className="text-lg font-semibold text-neutral-900 tabular-nums leading-none">{e.occurrences}</p>
                    <p className="text-[11px] text-neutral-500 mt-0.5">
                      {e.affected_users === 1 ? '1 user' : `${e.affected_users} users`}
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-xs text-neutral-500">
                  <span>First {formatDateInTz(e.first_seen_at, tz)}</span>
                  <span>Last {relativeAge(e.last_seen_at)}</span>
                  {e.last_org_name && <span className="break-words">{e.last_org_name}</span>}
                </div>

                <div className="flex flex-wrap gap-2 mt-3">
                  {e.stack && (
                    <button
                      onClick={() => setExpanded(expanded === e.id ? null : e.id)}
                      className="text-xs font-medium text-primary-600 hover:underline"
                    >
                      {expanded === e.id ? 'Hide stack' : 'Show stack'}
                    </button>
                  )}
                  <button
                    onClick={() => toggleResolved(e)}
                    className="text-xs font-medium text-neutral-600 hover:underline inline-flex items-center gap-1"
                  >
                    {e.resolved_at
                      ? (<><RotateCcw className="h-3 w-3" /> Reopen</>)
                      : (<><CheckCircle2 className="h-3 w-3" /> Mark resolved</>)}
                  </button>
                </div>

                {expanded === e.id && e.stack && (
                  <div className="mt-3">
                    {/* Said out loud because a minified frame otherwise looks
                        like the tool is broken rather than a known trade-off. */}
                    <p className="text-[11px] text-neutral-500 mb-1">
                      Minified — resolve against the .map files published beside the bundle.
                    </p>
                    <pre className="text-[11px] text-neutral-700 bg-neutral-50 border border-neutral-200 rounded-lg p-3 overflow-x-auto whitespace-pre">
                      {e.stack}
                    </pre>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {loading && !overview && tab === 'overview' && (
        <div className="flex items-center gap-2 text-sm text-neutral-500 py-8 justify-center">
          <Activity className="h-4 w-4 animate-pulse" />
          Loading platform data…
        </div>
      )}
    </div>
  );
}

function SearchBox({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400 pointer-events-none" />
      <input
        type="search"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-lg border border-neutral-300 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
      />
    </div>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return <th className={cn('text-left font-medium px-3 py-2 whitespace-nowrap', className)}>{children}</th>;
}

function Td({ children, className }: { children: React.ReactNode; className?: string; title?: string }) {
  return <td className={cn('px-3 py-2.5 align-top', className)}>{children}</td>;
}

function EmptyRow({ colSpan, label }: { colSpan: number; label: string }) {
  return (
    <tr><td colSpan={colSpan} className="px-3 py-8 text-center text-sm text-neutral-500">{label}</td></tr>
  );
}

function Pill({ children, tone }: { children: React.ReactNode; tone: 'neutral' | 'amber' | 'green' }) {
  const tones = {
    neutral: 'bg-neutral-100 text-neutral-600',
    amber: 'bg-amber-100 text-amber-800',
    green: 'bg-emerald-100 text-emerald-700',
  };
  return <span className={cn('inline-block px-2 py-0.5 rounded text-xs font-medium', tones[tone])}>{children}</span>;
}

export default PlatformOverviewPanel;
